import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Typography, Button, Chip } from '@mui/material';
import HeaderEntreprise from '../../header/headerEntreprise';
import Footer from '../../commun/Footer';
import Chatbox from '../../commun/ChatBox';
import GenericTable from '../../commun/GenericTable';
import ActionButtons from '../../commun/ActionButtons';
import Pagination from '../../commun/Pagination';
import CustomAlert from '../../commun/CustomAlert';
import { showDeleteConfirmation } from '../../commun/deletemsg';
import StatusTabs from '../../commun/StatusTabs';
import './MissionsEntreprise.css';

const MissionsEntreprise = () => {
  const [missions, setMissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMissionId, setEditingMissionId] = useState(null);
  const [editForm, setEditForm] = useState({ nomMission: '', status: '', remuneration: '', devise: '€' });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [page, setPage] = useState(1);
  const [displayMode, setDisplayMode] = useState('table');
  const [sortStatus, setSortStatus] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const entrepriseName = localStorage.getItem('entrepriseName') || 'Entreprise';
  const missionsPerPage = 5;

  useEffect(() => {
    const fetchMissions = async () => {
      const token = localStorage.getItem('authToken');
      const entrepriseId = localStorage.getItem('userId');
      if (!token || !entrepriseId) return;

      try {
        const res = await fetch(`${API_BASE}/api/missions/entreprise`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ entrepriseId }),
        });
        const data = await res.json();
        setMissions(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Erreur de chargement des missions', error);
      }
    };
    fetchMissions();
  }, [API_BASE]);

  const showAlert = (message, severity = 'success') => setAlert({ open: true, message, severity });

  const filteredMissions = missions
    .filter((m) =>
      (!sortStatus || m.status === sortStatus) &&
      m.nomMission?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const paginated = filteredMissions.slice((page - 1) * missionsPerPage, page * missionsPerPage);

  const columns = [
    {
      key: 'nomMission',
      label: 'Mission',
      render: (value, row) =>
        editingMissionId === row._id ? (
          <TextField
            value={editForm.nomMission}
            onChange={(e) => setEditForm((prev) => ({ ...prev, nomMission: e.target.value }))}
          />
        ) : (
          <span
            className="clickable-mission-name"
            onClick={() => navigate(`/Entreprise/Details/${row._id}`)}
          >
            {value}
          </span>
        )
    },
    {
      key: 'datePublication',
      label: 'Date publication',
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value, row) =>
        editingMissionId === row._id ? (
          <select
            value={editForm.status}
            onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="active">ACTIVE</option>
            <option value="inactive">INACTIVE</option>
          </select>
        ) : (
          <Chip
            label={value === 'active' ? 'ACTIVE' : 'INACTIVE'}
            style={{
              backgroundColor: value === 'active' ? '#2e7d32' : '#d32f2f',
              color: '#fff',
              fontWeight: 'bold',
            }}
          />
        )
    },
    {
      key: 'remuneration',
      label: 'Prime',
      render: (value, row) =>
        editingMissionId === row._id ? (
          <TextField
            type="number"
            value={editForm.remuneration}
            onChange={(e) => setEditForm((prev) => ({ ...prev, remuneration: e.target.value }))}
          />
        ) : (
          `${value} ${row.devise || ''}`
        )
    },
    {
      key: 'CandidatsEncours',
      label: 'Candidats en cours',
    },
    {
      key: 'createdByName',
      label: 'Créé par',
    },
  ];

  const tableData = paginated.map((m) => ({
    ...m,
    datePublication: new Date(m.createdAt).toLocaleDateString(),
  }));

  return (
    <Box>
      <HeaderEntreprise entrepriseName={entrepriseName} />

      <Box className="main-container">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Mes missions</Typography>
          <Button variant="contained" onClick={() => navigate('/Entreprise/Addmission')}>Ajouter mission</Button>
        </Box>

        <div className="filters-section-horizontal">
          <TextField
            label="Recherche"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-bar"
          />

          <StatusTabs
            statuses={{
              active: missions.filter((m) => m.status === 'active'),
              inactive: missions.filter((m) => m.status === 'inactive'),
            }}
            activeStatus={sortStatus}
            onStatusChange={(newStatus) => setSortStatus(newStatus)}
            userRole="entreprise"
            statusStylesSource={[
              { value: 'active', label: 'ACTIVE', style: { backgroundColor: '#2e7d32', color: '#fff' } },
              { value: 'inactive', label: 'INACTIVE', style: { backgroundColor: '#d32f2f', color: '#fff' } },
            ]}
            labelMapping={{
              active: 'ACTIVE',
              inactive: 'INACTIVE'
            }}
          />
        </div>

        <GenericTable
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          columns={columns}
          data={tableData}
          totalItems={filteredMissions.length}
          renderActions={(row) => {
            const isEditing = editingMissionId === row._id;
            return isEditing ? (
              <ActionButtons
                isEditing
                onSave={() => {
                  fetch(`${API_BASE}/api/missions/${row._id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(editForm),
                  })
                    .then((res) => res.json())
                    .then((updated) => {
                      setMissions((prev) =>
                        prev.map((m) => (m._id === row._id ? updated.mission : m))
                      );
                      setEditingMissionId(null);
                      showAlert('Mise à jour réussie');
                    });
                }}
                onCancel={() => setEditingMissionId(null)}
              />
            ) : (
              <ActionButtons
                onView={() => navigate(`/Entreprise/Details/${row._id}`)}   
                onDelete={() => {
                  showDeleteConfirmation({
                    onConfirm: async () => {
                      await fetch(`${API_BASE}/api/missions/${row._id}`, { method: 'DELETE' });
                      setMissions((prev) => prev.filter((m) => m._id !== row._id));
                    }
                  });
                }}
              />
            );
          }}
        />

        {displayMode === 'table' && (
          <Pagination
            page={page}
            setPage={setPage}
            totalItems={filteredMissions.length}
            itemsPerPage={missionsPerPage}
          />
        )}
      </Box>

      <Footer />
      <CustomAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />
      <Chatbox userRole="entreprise" />
    </Box>
  );
};

export default MissionsEntreprise;
